import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  styled,
  Chip,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Modal,
  Grid,
  Button,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { getApiData } from "../../utils/APIHelper";
import { Setting } from "../../utils/Setting";
import { useTokenValidation } from "../../components/hooks/useTokenValidation";
import SecurityPolicyModal from "../../components/SecurityPolicyModal";
import { ReactComponent as LeftArrowIcon } from "../../assets/svg/LeftArrowIcon.svg";
import { useSelector } from "react-redux";
import CInput from "../../components/CInput";
import CAutocomplete from "../../components/CAutocomplete";
import { toast } from "react-toastify";
import { useCRUDPermission } from "../../components/hooks/usePermission";
import Permission from "../../components/Permission";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  borderBottom: "none",
}));

const StyledChip = styled(Chip)(({ theme, status }) => ({
  borderRadius: "34px",
  fontSize: "12px",
  fontWeight: "500",
  minWidth: "70px",
  backgroundColor: status === "active" ? "#E8F5E8" : "#FFE8E8",
  color: status === "active" ? "#2E7D32" : "#D32F2F",
  border: "none",
}));

const StatusIndicator = styled(Box)(({ theme, isActive }) => ({
  width: "15px",
  height: "15px",
  borderRadius: "50%",
  backgroundColor: isActive ? "#4CAF50" : "#F44336",
  border: "3px solid #e5e5e5",
}));

const StyledHeading = styled("h2")({
  color: "#0D0D0E",
  fontFamily: "Inter Tight",
  fontSize: "24px",
  fontWeight: "500",
});

export default function UserManagement() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openAdd, setOpenAdd] = useState(false);
  const { userData } = useSelector((state) => state.auth);

  // Token validation hook
  const { showSecurityModal, handleSecurityModalClose } = useTokenValidation();

  // Check USERS CREATE permission
  const { hasPermission: hasUsersCreate } = useCRUDPermission("USERS", "CREATE");

    useEffect(() => {
    fetchUsers();
    }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await getApiData("api/contractor/users", "POST");
      
      if (response.success) {
        setUsers(response.data.contractor_users || []);
        setAnalytics(response.data.contractor_analytics || null);
      } else {
        setError("Failed to fetch users");
      }
    } catch (err) {
      console.error("Error fetching users:", err);
      setError("Error fetching users");
    } finally {
      setLoading(false);
    }
  };

  const getStatusText = (isDeleted, isBlocked) => {
    if (isDeleted) return "Deleted";
    if (isBlocked) return "Blocked";
    return "Active";
  };

  const getStatusColor = (isDeleted, isBlocked) => {
    if (isDeleted) return "#9E9E9E";
    if (isBlocked) return "#F44336";
    return "#4CAF50";
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <div style={{ height: "100%" }}>
      {/* Security Policy Modal */}
      <SecurityPolicyModal
        open={showSecurityModal}
        onConfirm={handleSecurityModalClose}
      />

      <Box
        sx={{
          typography: "body1",
          backgroundColor: "#fff",
          height: "100%",
          borderRadius: "6px",
          fontFamily: "Inter Tight",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "4vh 2vw 2vh 2vw",
            background:
              "linear-gradient(180deg, rgba(77, 107, 246, 0.30) 0%, rgba(255, 255, 255, 0.00) 100%)",
            display: "flex",
            alignItems: "center",
            gap: "20px",
          }}
        >
          <LeftArrowIcon
            onClick={() => navigate("/account-setting")}
            style={{ cursor: "pointer" }}
          />
          <StyledHeading>User Management</StyledHeading>
          <div style={{ marginLeft: "auto" }}>
            <Permission resource="USERS" action="CREATE">
              <Button
                variant="contained"
                onClick={() => setOpenAdd(true)}
                sx={{ borderRadius: "14px", px: 3.5, py: 1.2, fontWeight: 700, boxShadow: "none" }}
              >
                Add User
              </Button>
            </Permission>
          </div>
        </div>

        <Box sx={{ padding: "0 2vw 20px 2vw" }}>
          {/* Analytics Section */}
          {analytics && (
        <Box sx={{ marginBottom: "30px" }}>
         
          <Box sx={{ 
            display: "flex", 
            gap: "40px", 
            alignItems: "flex-start",
            flexWrap: "wrap"
          }}>
            {/* Customers Section */}
            <Box sx={{ minWidth: "150px" }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: "#9E9E9E", 
                  fontSize: "14px",
                  marginBottom: "8px",
                  fontWeight: "500"
                }}
              >
                Customers
              </Typography>
              <Typography 
                variant="h4" 
                sx={{ 
                  fontWeight: "700",
                  fontSize: "32px",
                  color: "#0D0D0E",
                  marginBottom: "4px"
                }}
              >
                {analytics.total_users}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <Box
                  sx={{
                    width: 0,
                    height: 0,
                    borderLeft: "4px solid transparent",
                    borderRight: "4px solid transparent",
                    borderBottom: "6px solid #4CAF50",
                  }}
                />
                <Typography 
                  sx={{ 
                    fontSize: "12px", 
                    color: "#9E9E9E",
                    fontWeight: "400"
                  }}
                >
                  - than last week
                </Typography>
              </Box>
            </Box>

            {/* Active Section */}
            <Box sx={{ minWidth: "200px", flex: 1 }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: "#9E9E9E", 
                  fontSize: "14px",
                  marginBottom: "8px",
                  fontWeight: "500"
                }}
              >
                Active
              </Typography>
              <Typography 
                variant="h4" 
                sx={{ 
                  fontWeight: "700",
                  fontSize: "32px",
                  color: "#0D0D0E",
                  marginBottom: "12px"
                }}
              >
                {analytics.active_users}
              </Typography>
              
              {/* Progress Bar */}
              <Box sx={{ 
                width: "100%", 
                height: "8px", 
                backgroundColor: "#F5F5F5", 
                borderRadius: "4px",
                overflow: "hidden",
                position: "relative"
              }}>
                <Box
                  sx={{
                    height: "100%",
                    width: `${(analytics.active_users / analytics.total_users) * 100}%`,
                    backgroundColor: "#4CAF50",
                    borderRadius: "4px 0 0 4px",
                    position: "absolute",
                    left: 0,
                  }}
                />
                <Box
                  sx={{
                    height: "100%",
                    width: `${((analytics.total_users - analytics.active_users) / analytics.total_users) * 100}%`,
                    backgroundColor: "#FF5722",
                    borderRadius: "0 4px 4px 0",
                    position: "absolute",
                    right: 0,
                  }}
                />
              </Box>
            </Box>

            {/* Dormant Section */}
            <Box sx={{ minWidth: "150px" }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: "#9E9E9E", 
                  fontSize: "14px",
                  marginBottom: "8px",
                  fontWeight: "500"
                }}
              >
                Deleted
              </Typography>
              <Typography 
                variant="h4" 
                sx={{ 
                  fontWeight: "700",
                  fontSize: "32px",
                  color: "#0D0D0E"
                }}
              >
                {analytics.total_users - analytics.active_users}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      {/* Users Table */}
      <TableContainer
        component={Paper}
        sx={{
          width: "100%",
          overflow: "none !important",
          boxShadow: "none",
          maxHeight: "70vh",
          overflowY: "scroll",
          scrollbarWidth: "none",
        }}
      >
        <Table
          sx={{
            "& thead": {
              "& th": {
                color: "#949DAA",
                fontWeight: "500",
              },
            },
            "& tbody": {
              "& tr:hover": {
                backgroundColor: "#f7f7f7",
              },
              "& tr": {
                borderBottom: "none",
              },
            },
          }}
        >
          <TableHead>
            <TableRow>
              <StyledTableCell>Username</StyledTableCell>
              <StyledTableCell>Email</StyledTableCell>
              <StyledTableCell>Role</StyledTableCell>
              <StyledTableCell>isActive</StyledTableCell>
              <StyledTableCell>Blocked</StyledTableCell>
            </TableRow>
          </TableHead>
              <TableBody>
            {users.map((user) => (
              <TableRow key={user.id}>
                <StyledTableCell>
                  <Box display="flex" alignItems="center">
                    {user.profile_url ? (
                      <Box
                        component="img"
                        src={user.profile_url}
                        alt="Profile"
                        sx={{
                          width: 35,
                          height: 35,
                          borderRadius: "50%",
                          border: "2px solid white",
                          boxShadow: "0px 4px 2.67px 0px #00000026",
                          marginRight: 1,
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: 35,
                          height: 35,
                          borderRadius: "50%",
                          backgroundColor: "#e0e0e0",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          marginRight: 1,
                          fontSize: "14px",
                          fontWeight: "600",
                          color: "#666",
                        }}
                      >
                        {user.username?.charAt(0)?.toUpperCase() || "U"}
                      </Box>
                    )}
                    <div>
                      <Typography
                        sx={{
                          fontSize: "14px",
                          fontWeight: "600",
                          color: "#0D0D0E",
                        }}
                      >
                        {user.username || "N/A"}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "12px",
                          color: "#949DAA",
                        }}
                      >
                        ID: {user.id}
                      </Typography>
          </div>
                  </Box>
                </StyledTableCell>

                <StyledTableCell>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      color: "#0D0D0E",
                    }}
                  >
                    {user.email}
                  </Typography>
                </StyledTableCell>

                <StyledTableCell>
                  <StyledChip
                    label={user.role}
                    size="small"
                    sx={{
                      backgroundColor: "#E3F2FD",
                      color: "#1976D2",
                    }}
                  />
                </StyledTableCell>

                <StyledTableCell>
                  <Box display="flex" alignItems="center">
                    <StatusIndicator 
                      isActive={!user.is_deleted && !user.is_block} 
                    />
                    <Typography
                      sx={{
                        fontSize: "14px",
                        marginLeft: "8px",
                        fontWeight: "500",
                      }}
                    >
                      {!user.is_deleted && !user.is_block ? "Yes" : "No"}
                    </Typography>
                  </Box>
                </StyledTableCell>

                <StyledTableCell>
                  <Box display="flex" alignItems="center">
                    <StatusIndicator 
                      isActive={user.is_block} 
                    />
                    <Typography
                      sx={{
                        fontSize: "14px",
                        marginLeft: "8px",
                        fontWeight: "500",
                        color: user.is_block ? "#D32F2F" : "#2E7D32",
                      }}
                    >
                      {user.is_block ? "Yes" : "No"}
                    </Typography>
                  </Box>
                </StyledTableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

          {users.length === 0 && (
            <Box display="flex" justifyContent="center" alignItems="center" height="200px">
              <Typography color="textSecondary">No users found</Typography>
            </Box>
          )}
        </Box>
      </Box>
      {/* Add User Modal */}
      <Modal open={openAdd} onClose={() => setOpenAdd(false)} disableAutoFocus>
        <Grid
          item
          xs={10}
          sm={8}
          md={6}
          lg={6}
          sx={{
            backgroundColor: "#FFF",
            borderRadius: 3,
            maxWidth: 780,
            minWidth: 360,
            width: "60vw",
            mx: "auto",
            my: 4,
            outline: "none",
          }}
        >
          <Grid container>
            <Grid item xs={12} sx={{ p: 3, borderBottom: "1px solid #F2F4F7" }}>
              <Typography sx={{ color: "#030F1C", fontFamily: "Inter Tight", fontSize: 28, lineHeight: "36px", fontWeight: 600 }}>
                Add User
              </Typography>
            </Grid>
            <AddUserForm
              roleValue={userData?.role || "contractor"}
              onCancel={() => setOpenAdd(false)}
              onCreated={async () => {
                setOpenAdd(false);
                await fetchUsers();
              }}
            />
          </Grid>
        </Grid>
      </Modal>
    </div>
  );
}

function AddUserForm({ roleValue, onCancel, onCreated }) {
  const roleOptions = [
    { value: "contractor_finance_manager", label: "Finance Manager" },
    { value: "contractor_project_manager", label: "Project Manager" },
    { value: "contractor_admin", label: "Admin" },
    { value: "contractor_site_supervisor", label: "Site Supervisor" }
  ];
  
  const [form, setForm] = useState({ username: "", email: "", phone_code: "971", phone_no: "", role: "" });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    // For phone_code, remove '+' sign and limit to 3 characters
    if (name === "phone_code") {
      const cleanedValue = value.replace(/^\+/, "").slice(0, 3);
      setForm((p) => ({ ...p, [name]: cleanedValue }));
    } else {
      setForm((p) => ({ ...p, [name]: value }));
    }
  };

  const handleSubmit = async () => {
    // Remove '+' sign from phone_code if present (database expects only digits, max 3 chars)
    const phoneCode = form.phone_code ? form.phone_code.replace(/^\+/, "") : "";
    
    // Validate phone_code length (database column accepts max 3 characters)
    if (phoneCode && phoneCode.length > 3) {
      toast.error("Phone code must be 3 characters or less (e.g., 971)");
      return;
    }
    
    setSubmitting(true);
    try {
      const payload = { ...form, phone_code: phoneCode, device_type: "web", notification: 1 };
      const res = await getApiData(
        Setting.endpoints.contractorAddUser || "api/contractor/add-user",
        "POST",
        payload,
        { "Content-Type": "application/json" }
      );
      if (res?.success) {
        toast.success(res?.message || "User created successfully");
        onCreated();
      } else if (res?.message) {
        toast.error(res.message);
      } else {
        toast.error("Failed to create user");
      }
    } catch (e) {
      toast.error(e?.message || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Grid container sx={{ p: 3 }} spacing={2}>
      <Grid item xs={12}>
        <CInput label="Username" name="username" placeholder="Enter User Name" value={form.username} onChange={handleChange} isWhiteBackground />
      </Grid>
      <Grid item xs={12}>
        <CInput label="Email" name="email" placeholder="Enter Email Address" value={form.email} onChange={handleChange} isWhiteBackground />
      </Grid>
      <Grid item xs={4}>
        <CInput label="Code" name="phone_code" placeholder="971" value={form.phone_code} onChange={handleChange} isWhiteBackground maxLength={3} />
      </Grid>
      <Grid item xs={8}>
        <CInput label="Phone" name="phone_no" placeholder="Phone" value={form.phone_no} onChange={handleChange} isWhiteBackground />
      </Grid>
      <Grid item xs={12}>
        <CAutocomplete
          label="Role"
          options={roleOptions}
          value={form.role ? roleOptions.find(opt => opt.value === form.role) : null}
          onChange={(event, newValue) => {
            setForm((p) => ({ ...p, role: newValue?.value || "" }));
          }}
          getOptionLabel={(option) => option.label}
          placeholder="Select Role"
        />
      </Grid>
      <Grid item xs={12} sx={{ display: "flex", justifyContent: "space-between", p: 2, pt: 0 }}>
        <Button variant="outlined" onClick={onCancel} sx={{ borderRadius: "14px" }}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={submitting} sx={{ borderRadius: "14px" }}>Add User</Button>
      </Grid>
    </Grid>
  );
}